import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
	Add,
	Check,
	Close,
	Delete,
	DragIndicator,
	Error,
	Restore,
	Undo,
} from "@mui/icons-material";
import {
	Alert,
	AppBar,
	Box,
	Button,
	IconButton,
	LinearProgress,
	Tab,
	Tabs,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Toolbar,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import uuid from "react-uuid";
import {
	Person,
	QueryStatus,
	SessionState,
	SettingsObject,
	postAction,
} from "../utils";
import Editor from "react-simple-code-editor";
import Prism, { highlight } from "prismjs";
import "prismjs/components/prism-json.min";
import "prismjs/themes/prism-solarizedlight.css";

enum SettingsTabSelector {
	STAFF = 0,
	CASE = 1,
}

interface SettingsTabProps {
	onClose: () => void;
	settings: SettingsObject;
	people: Person[];
	sessionState: SessionState;
	setSessionState: (arg0: SessionState) => void;
	refetchSettings: () => void;
	saveKeyPressed: boolean;
}

interface DraggableListProps {
	listId: string;
	list: { value: string; id: string }[];
	setList: (arg0: { value: string; id: string }[]) => void;
}

interface PeopleListProps {
	originalList: Person[];
	peopleList: Person[];
	setPeopleList: (arg0: Person[]) => void;
}

function areArraysEqual(a: string[], b: string[]) {
	if (a === undefined && b !== undefined) {
		return false;
	}
	if (a !== undefined && b === undefined) {
		return false;
	}
	if (a === undefined && b === undefined) {
		return true;
	}

	if (a.length !== b.length) {
		return false;
	}

	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}

	return true;
}

function DraggableList({ listId, list, setList }: DraggableListProps) {
	return (
		<Box
			sx={{
				display: "table",
				border: (theme) => `1px solid ${theme.palette.grey[800]}`,
				borderRadius: "4px",
			}}
		>
			<DragDropContext
				onDragEnd={({ destination, source, draggableId }) => {
					if (!destination) {
						return;
					}
					if (
						destination.droppableId === source.droppableId &&
						destination.index === source.index
					) {
						return;
					}
					const newList = Array.from(list);
					const value = list.find(({ id }) => id === draggableId)
						?.value as string;
					newList.splice(source.index, 1);
					newList.splice(destination.index, 0, {
						value,
						id: draggableId,
					});

					setList(newList);
				}}
			>
				<Droppable key={listId} droppableId={listId}>
					{(provided) => (
						<Box
							sx={{ display: "table" }}
							ref={provided.innerRef}
							{...provided.droppableProps}
						>
							{list.map(({ value, id }, index) => (
								<Draggable
									key={id}
									draggableId={id}
									index={index}
								>
									{(provided) => (
										<div
											{...provided.draggableProps}
											ref={provided.innerRef}
										>
											<Box
												sx={{
													margin: 0,
													paddingTop: 1,
													paddingBottom: 1,
													backgroundColor: (theme) =>
														theme.palette.grey[900],
													display: "flex",
													alignItems: "center",
													border: (theme) =>
														`0.5px solid ${theme.palette.grey[800]}`,
												}}
											>
												<div
													{...provided.dragHandleProps}
												>
													<DragIndicator
														sx={{
															color: (theme) =>
																theme.palette
																	.grey[800],
															fontSize: "32px",
														}}
													/>
												</div>
												<TextField
													sx={{ width: "300px" }}
													value={value}
													onChange={(e) => {
														const newList =
															Array.from(list);
														newList[index] = {
															value: e.target
																.value,
															id,
														};
														setList(newList);
													}}
												/>
												<Tooltip title="Delete">
													<IconButton
														size="small"
														sx={{
															margin: 1,
															float: "right",
														}}
														onClick={() => {
															const newList =
																Array.from(
																	list
																);
															newList.splice(
																index,
																1
															);
															setList(newList);
														}}
													>
														<Delete />
													</IconButton>
												</Tooltip>
											</Box>
										</div>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</Box>
					)}
				</Droppable>
			</DragDropContext>
			<Tooltip title="Add New Field">
				<IconButton
					sx={{
						float: "right",
						m: 1,
						backgroundColor: (theme) => theme.palette.primary.main,
					}}
					onClick={() =>
						setList([
							...list,
							{
								id: uuid(),
								value: "",
							},
						])
					}
				>
					<Add />
				</IconButton>
			</Tooltip>
		</Box>
	);
}

function PeopleList({
	originalList,
	peopleList,
	setPeopleList,
}: PeopleListProps) {
	return (
		<Box
			sx={{
				display: "table",
				border: (theme) => `1px solid ${theme.palette.grey[800]}`,
				borderRadius: "4px",
			}}
		>
			<Box sx={{ display: "table" }}>
				{peopleList.map((person, index) => (
					<Box
						sx={{
							margin: 0,
							paddingTop: 1,
							paddingBottom: 1,
							backgroundColor:
								person._id === ""
									? "rgb(0, 87, 45)"
									: person.archived
									? "rgb(87, 45, 0)"
									: JSON.stringify(
											originalList.find(
												({ _id }) => _id === person._id
											)
									  ) !== JSON.stringify(person)
									? "rgb(0, 45, 87)"
									: (theme) => theme.palette.grey[900],
							display: "flex",
							alignItems: "center",
							border: (theme) =>
								`0.5px solid ${theme.palette.grey[800]}`,
						}}
					>
						<ToggleButtonGroup
							sx={{ margin: 1 }}
							value={person.roles}
							onChange={(_, value) => {
								if (value !== null) {
									const newList = Array.from(peopleList);
									newList[index] = {
										...person,
										roles: value,
									};
									setPeopleList(newList);
								}
							}}
						>
							<ToggleButton value="attorney">
								Attorney
							</ToggleButton>
							<ToggleButton value="staff">Staff</ToggleButton>
						</ToggleButtonGroup>
						<TextField
							placeholder="Name"
							sx={{
								width: "300px",
								marginRight: 1,
								marginLeft: 1,
							}}
							value={person.name}
							onChange={(e) => {
								const newList = Array.from(peopleList);
								newList[index] = {
									...person,
									name: e.target.value,
								};
								setPeopleList(newList);
							}}
						/>
						<TextField
							placeholder="Email"
							sx={{ width: "300px" }}
							value={person.email}
							onChange={(e) => {
								const newList = Array.from(peopleList);
								newList[index] = {
									...person,
									email: e.target.value,
								};
								setPeopleList(newList);
							}}
						/>
						{person.archived ? (
							<Tooltip title="Undelete">
								<IconButton
									size="small"
									sx={{
										margin: 1,
										float: "right",
									}}
									onClick={() => {
										const newList = Array.from(peopleList);
										newList[index] = {
											...person,
											archived: false,
										};
										setPeopleList(newList);
									}}
								>
									<Undo />
								</IconButton>
							</Tooltip>
						) : (
							<Tooltip title="Delete">
								<IconButton
									size="small"
									sx={{
										margin: 1,
										float: "right",
									}}
									onClick={() => {
										const newList = Array.from(peopleList);
										if (person._id === "") {
											newList.splice(index, 1);
										} else {
											newList[index] = {
												...person,
												archived: true,
											};
										}
										setPeopleList(newList);
									}}
								>
									<Delete />
								</IconButton>
							</Tooltip>
						)}
					</Box>
				))}
			</Box>
			<Tooltip title="Add New Field">
				<IconButton
					sx={{
						float: "right",
						m: 1,
						backgroundColor: (theme) => theme.palette.primary.main,
					}}
					onClick={() =>
						setPeopleList([
							...peopleList,
							{
								archived: false,
								name: "",
								email: "",
								roles: [],
								_id: "",
							},
						])
					}
				>
					<Add />
				</IconButton>
			</Tooltip>
		</Box>
	);
}

export function SettingsTab({
	people,
	settings,
	sessionState,
	setSessionState,
	refetchSettings,
	onClose,
	saveKeyPressed,
}: SettingsTabProps) {
	const [tab, setTab] = useState<SettingsTabSelector>(
		SettingsTabSelector.STAFF
	);

	const [peopleList, setPeopleList] = useState<Person[]>([]);

	/*const [attorneyList, setAttorneyList] = useState<
		{ value: string; id: string }[]
	>([]);*/
	const [accountTypes, setAccountTypes] = useState<
		{ value: string; id: string }[]
	>([]);
	/*
	const [staffList, setStaffList] = useState<{ value: string; id: string }[]>(
		[]
	);*/
	const [caseTypes, setCaseTypes] = useState<{ value: string; id: string }[]>(
		[]
	);
	const [statusTypes, setStatusTypes] = useState<
		{ value: string; id: string }[]
	>([]);
	const [exemptionApplicationTypes, setExemptionApplicationTypes] = useState<
		{ value: string; id: string }[]
	>([]);
	const [exemptionStatusTypes, setExemptionStatusTypes] = useState<
		{ value: string; id: string }[]
	>([]);

	const [advancedSettings, setAdvancedSettings] = useState<string>("");

	const [postStatus, setPostStatus] = useState<QueryStatus>(QueryStatus.IDLE);

	const resetSettings = () => {
		setPeopleList([...people.filter(({ archived }) => !archived)]);
		//setAttorneyList((settings.attorneyList || []).map((v: string) => ({ value: v, id: uuid() })) || []);
		setAccountTypes(
			(settings.accountTypes || []).map((v: string) => ({
				value: v,
				id: uuid(),
			})) || []
		);
		//setStaffList((settings.staffList || []).map((v: string) => ({ value: v, id: uuid() })) || []);
		setCaseTypes(
			(settings.caseTypes || []).map((v: string) => ({
				value: v,
				id: uuid(),
			})) || []
		);
		setStatusTypes(
			(settings.statusTypes || []).map((v: string) => ({
				value: v,
				id: uuid(),
			})) || []
		);
		setExemptionApplicationTypes(
			(settings.exemptionApplicationTypes || []).map((v: string) => ({
				value: v,
				id: uuid(),
			})) || []
		);
		setExemptionStatusTypes(
			(settings.exemptionStatusTypes || []).map((v: string) => ({
				value: v,
				id: uuid(),
			})) || []
		);
		setAdvancedSettings(JSON.stringify(settings.layoutByCaseType, null, 2));
	};

	useEffect(resetSettings, [settings, people]);

	let isJsonEdited = false;
	let jsonError = false;
	try {
		isJsonEdited =
			JSON.stringify(JSON.parse(advancedSettings)) !==
			JSON.stringify(settings.layoutByCaseType);
	} catch (e) {
		jsonError = true;
	}

	const isEdited =
		!areArraysEqual(
			people
				.filter(({ archived }) => !archived)
				.map((person) => JSON.stringify(person)),
			peopleList.map((person) => JSON.stringify(person))
		) ||
		!areArraysEqual(
			accountTypes.map(({ value }) => value),
			settings.accountTypes
		) ||
		!areArraysEqual(
			caseTypes.map(({ value }) => value),
			settings.caseTypes
		) ||
		!areArraysEqual(
			statusTypes.map(({ value }) => value),
			settings.statusTypes
		) ||
		!areArraysEqual(
			exemptionApplicationTypes.map(({ value }) => value),
			settings.exemptionApplicationTypes
		) ||
		!areArraysEqual(
			exemptionStatusTypes.map(({ value }) => value),
			settings.exemptionStatusTypes
		) ||
		isJsonEdited;

	const updateAction = async () => {
		await postAction(
			{
				action: "set-settings",
				id: settings["_id"],
				data: {
					...settings,
					_id: undefined,
					accountTypes: accountTypes.map(({ value }) => value),
					caseTypes: caseTypes.map(({ value }) => value),
					statusTypes: statusTypes.map(({ value }) => value),
					exemptionApplicationTypes: exemptionApplicationTypes.map(
						({ value }) => value
					),
					exemptionStatusTypes: exemptionStatusTypes.map(
						({ value }) => value
					),
					layoutByCaseType: JSON.parse(advancedSettings),
				},
			},
			sessionState,
			setSessionState,
			() => {
				setPostStatus(QueryStatus.SUCCESS);
				refetchSettings();
			},
			(e) => {
				console.log(e);
				setPostStatus(QueryStatus.FAILURE);
			}
		);
		peopleList.forEach(async (person) => {
			if (person._id === "") {
				await postAction(
					{
						action: "add-person",
						data: { ...person, _id: undefined },
					},
					sessionState,
					setSessionState,
					() => {
						setPostStatus(QueryStatus.SUCCESS);
						refetchSettings();
					},
					(e) => {
						console.log(e);
						setPostStatus(QueryStatus.FAILURE);
					}
				);
			} else {
				await postAction(
					{
						action: "update-person",
						id: person._id,
						data: { ...person, _id: undefined },
					},
					sessionState,
					setSessionState,
					() => {
						setPostStatus(QueryStatus.SUCCESS);
						refetchSettings();
					},
					(e) => {
						console.log(e);
						setPostStatus(QueryStatus.FAILURE);
					}
				);
			}
		});
	};

	useEffect(() => {
		if (saveKeyPressed === true) {
			if (!isEdited || jsonError) {
				return;
			}
			updateAction();
		}
	}, [saveKeyPressed]);

	return (
		<>
			<AppBar position="sticky">
				<Toolbar>
					<Typography
						variant="h6"
						color="inherit"
						component="div"
						sx={{ flex: 1 }}
					>
						Settings
					</Typography>
					<Tabs value={tab} onChange={(_, value) => setTab(value)}>
						<Tab label="Staff Settings"></Tab>
						<Tab label="Case Settings"></Tab>
					</Tabs>
					<Tooltip title="Undo Changes">
						<IconButton
							sx={{ m: 1 }}
							disabled={!isEdited && !jsonError}
							onClick={resetSettings}
						>
							<Restore />
						</IconButton>
					</Tooltip>
					<Button
						variant="contained"
						disabled={!isEdited || jsonError}
						onClick={updateAction}
					>
						Update Settings
					</Button>
					<Tooltip title="Close Window">
						<IconButton
							onClick={() => onClose()}
							sx={{ color: "inherit" }}
						>
							<Close />
						</IconButton>
					</Tooltip>
				</Toolbar>
				{postStatus === QueryStatus.LOADING && (
					<LinearProgress
						sx={{
							position: "absolute",
							width: "100%",
							bottom: "-4px",
						}}
					/>
				)}
				{postStatus === QueryStatus.FAILURE && (
					<Alert
						variant="filled"
						severity="error"
						onClose={() => setPostStatus(QueryStatus.IDLE)}
					>
						An error occurred, please try again.
					</Alert>
				)}
			</AppBar>
			<Box sx={{ position: "relative", width: "100%" }}>
				{tab === 0 && (
					<>
						<Box sx={{ display: "flex", flexWrap: "wrap" }}>
							<Box sx={{ margin: 2 }}>
								<PeopleList
									originalList={people}
									peopleList={peopleList}
									setPeopleList={setPeopleList}
								/>
							</Box>
						</Box>
					</>
				)}
				{tab === 1 && (
					<>
						<Box sx={{ display: "flex", flexWrap: "wrap" }}>
							<Box sx={{ margin: 2 }}>
								<Typography
									sx={{
										m: 1,
										color: (theme) =>
											theme.palette.text.primary,
										fontWeight: "bold",
									}}
								>
									Account Types
								</Typography>
								<DraggableList
									listId="accountTypes"
									list={accountTypes}
									setList={setAccountTypes}
								/>
							</Box>
							<Box sx={{ margin: 2 }}>
								<Typography
									sx={{
										m: 1,
										color: (theme) =>
											theme.palette.text.primary,
										fontWeight: "bold",
									}}
								>
									Case Types
								</Typography>
								<DraggableList
									listId="caseTypes"
									list={caseTypes}
									setList={setCaseTypes}
								/>
							</Box>
							<Box sx={{ margin: 2 }}>
								<Typography
									sx={{
										m: 1,
										color: (theme) =>
											theme.palette.text.primary,
										fontWeight: "bold",
									}}
								>
									Case Status Types
								</Typography>
								<DraggableList
									listId="statusTypes"
									list={statusTypes}
									setList={setStatusTypes}
								/>
							</Box>
							<Box sx={{ margin: 2 }}>
								<Typography
									sx={{
										m: 1,
										color: (theme) =>
											theme.palette.text.primary,
										fontWeight: "bold",
									}}
								>
									Exemption Application Types
								</Typography>
								<DraggableList
									listId="exemptionApplicationTypes"
									list={exemptionApplicationTypes}
									setList={setExemptionApplicationTypes}
								/>
							</Box>
							<Box sx={{ margin: 2 }}>
								<Typography
									sx={{
										m: 1,
										color: (theme) =>
											theme.palette.text.primary,
										fontWeight: "bold",
									}}
								>
									Exemption Status Types
								</Typography>
								<DraggableList
									listId="exemptionStatusTypes"
									list={exemptionStatusTypes}
									setList={setExemptionStatusTypes}
								/>
							</Box>
						</Box>
						<Box
							sx={{
								margin: 2,
							}}
						>
							<Typography
								sx={{
									m: 1,
									color: (theme) =>
										theme.palette.text.primary,
									fontWeight: "bold",
								}}
							>
								Advanced Settings
							</Typography>
							{jsonError ? (
								<Error
									fontSize="large"
									sx={{
										float: "right",
										color: (theme) =>
											theme.palette.error.main,
									}}
								/>
							) : (
								<Check
									fontSize="large"
									sx={{
										float: "right",
										color: (theme) =>
											theme.palette.info.main,
									}}
								/>
							)}
							<Box
								sx={{
									border: (theme) =>
										`1px solid ${theme.palette.grey[800]}`,
									borderRadius: "4px",
									background: "#fff",
									maxHeight: "500px",
									overflow: "auto",
								}}
							>
								<Editor
									highlight={(code) =>
										highlight(
											code,
											Prism.languages.json,
											"json"
										)
									}
									onValueChange={setAdvancedSettings}
									value={advancedSettings}
									padding={"10px"}
									style={{
										fontFamily:
											'"Fira code", "Fira Mono", monospace',
										fontSize: 12,
									}}
								/>
							</Box>
						</Box>
					</>
				)}
			</Box>
		</>
	);
}
